/**
 * Slang Sandbox - Main Entry Point
 */

import { ShaderEditor } from './utils/editor.js';
import { getCompiler } from './utils/slang-compiler.js';
import { WebGPURenderer } from './utils/webgpu-renderer.js';
import { LayoutManager } from './utils/layout-manager.js';
import { ErrorDisplay } from './utils/error-display.js';
import { readFromUrl, updateUrlMultiPass } from './utils/url-state.js';
import { CaptureManager } from './utils/capture-manager.js';
import { UIEvents } from './utils/ui-events.js';
import { loadSettings, updateSetting } from './utils/settings.js';
import { PassManager, PASS_IDS } from './utils/pass-manager.js';
import { getSampleById, isMultiPass } from './data/samples.js';
import { DEFAULT_SHADER, DEFAULT_BUFFER_SHADER } from './data/default-shaders.js';
import { generatePresetPreview } from './utils/texture-preview.js';
import { UniformManager } from './utils/uniform-manager.js';

class App {
  constructor() {
    this.editor = null;
    this.compiler = null;
    this.renderer = null;
    this.layoutManager = null;
    this.errorDisplay = null;
    this.captureManager = null;
    this.uiEvents = null;
    this.passManager = new PassManager();
    this.uniformManager = new UniformManager();
    this.settings = loadSettings();
  }

  async init() {
    // Initialize editor with saved theme
    const editorContainer = document.getElementById('editor-container');
    this.editor = new ShaderEditor(editorContainer, {
      defaultCode: DEFAULT_SHADER,
      onCompile: (code) => this.compile(code),
      theme: this.settings.editorTheme,
    });
    await this.editor.init();

    // Initialize error display
    this.errorDisplay = new ErrorDisplay();
    this.errorDisplay.init();

    // Setup UI events
    this.setupUIEvents();

    // Initialize WebGPU renderer
    await this.initRenderer();

    // Initialize layout manager with saved settings
    this.layoutManager = new LayoutManager({
      onResize: () => this.renderer?.resize(),
      onLayoutChange: (state) => {
        updateSetting('layout', state.layout);
        updateSetting('swapped', state.swapped);
      },
      initialLayout: this.settings.layout,
      initialSwapped: this.settings.swapped,
    });
    this.layoutManager.init();

    // Initialize theme selector
    this.initThemeSelector();

    // Initialize compiler
    try {
      this.compiler = await getCompiler();

      // Load state from URL or use defaults
      const urlResult = await readFromUrl();
      if (urlResult.error) {
        this.errorDisplay.show(urlResult.error + ', loading defaults');
      }
      const urlState = urlResult.data;
      if (urlState && typeof urlState === 'object' && urlState.v === 1) {
        // Multi-pass state from URL
        this.passManager.deserialize(urlState);
        // Restore buffer tabs in UI
        for (const bufferId of this.passManager.getEnabledBuffers()) {
          this.uiEvents.addBufferTab(bufferId, PassManager.getBufferLetter(bufferId));
        }
        this.uiEvents.setAddBufferButtonVisible(this.passManager.canAddBuffer());
        // Set renderer enabled buffers
        this.renderer?.setEnabledBuffers(this.passManager.getEnabledBuffers());
        // Set active pass
        const activePass = this.passManager.getActivePass();
        this.uiEvents.setActivePassTab(activePass);
        // Load code for active pass
        const activeCode = this.passManager.getCode(activePass);
        this.editor.setCode(activeCode || this.getDefaultCodeForPass(activePass));
        // Restore uniforms if present
        if (urlState.u) {
          this.uniformManager.deserialize(urlState.u);
          this.uiEvents.refreshUniformList();
        }
        // Compile all passes
        await this.compileAllPasses();
      } else if (urlState && typeof urlState === 'string') {
        // Legacy single-pass URL (backward compatibility)
        this.passManager.setCode(PASS_IDS.MAIN, urlState);
        this.editor.setCode(urlState);
        this.renderer?.setEnabledBuffers(this.passManager.getEnabledBuffers());
        await this.compilePass(PASS_IDS.MAIN, urlState);
      } else {
        // No URL state, use defaults (Main only)
        this.passManager.setCode(PASS_IDS.MAIN, DEFAULT_SHADER);
        this.editor.setCode(DEFAULT_SHADER);
        this.renderer?.setEnabledBuffers(this.passManager.getEnabledBuffers());
        await this.compilePass(PASS_IDS.MAIN, DEFAULT_SHADER);
      }

      // Enable auto-compile after initial load
      this.editor.enableAutoCompile();

      // Register beforeunload handler for cleanup
      window.addEventListener('beforeunload', () => this.destroy());
    } catch (err) {
      this.errorDisplay.show(`Compiler initialization error: ${err.message}`);
    } finally {
      // Hide loading overlay
      this.hideLoadingOverlay();
    }
  }

  /**
   * Hide the loading overlay with fade-out animation
   */
  hideLoadingOverlay() {
    const overlay = document.getElementById('loading-overlay');
    if (overlay) {
      overlay.classList.add('hidden');
      // Remove from DOM after transition
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    }
  }

  /**
   * Clean up all resources
   */
  destroy() {
    // Stop renderer first
    this.renderer?.stop();
    this.renderer?.destroy();
    this.renderer = null;

    // Clean up managers
    this.layoutManager?.destroy();
    this.layoutManager = null;

    this.errorDisplay?.destroy();
    this.errorDisplay = null;

    // Clean up capture manager
    if (this.captureManager?.isRecording) {
      this.captureManager.stopRecording();
    }
    this.captureManager = null;

    // Clean up editor
    this.editor = null;

    // Clean up compiler reference
    this.compiler = null;

    // Clean up uniform manager
    this.uniformManager = null;
  }

  /**
   * Get default code for a pass
   * @param {string} passId
   * @returns {string}
   */
  getDefaultCodeForPass(passId) {
    return passId === PASS_IDS.MAIN ? DEFAULT_SHADER : DEFAULT_BUFFER_SHADER;
  }

  async initRenderer() {
    // Check WebGPU support
    if (!WebGPURenderer.isSupported()) {
      this.errorDisplay.show('WebGPU is not supported in this browser.\nPlease use Chrome 113+, Edge 113+, or Firefox Nightly with WebGPU enabled.');
      return;
    }

    const canvas = document.getElementById('preview-canvas');
    this.renderer = new WebGPURenderer(canvas);

    // Setup callbacks
    this.renderer.onError = (message) => {
      this.errorDisplay.show(message);
    };

    // Initialize
    const success = await this.renderer.init();
    if (success) {
      // Connect uniform manager to renderer
      this.renderer.setUniformManager(this.uniformManager);

      // Initialize capture manager
      this.captureManager = new CaptureManager(canvas);
      this.setupCaptureCallbacks();

      // Set up frame callback for recording
      this.renderer.setOnFrameCallback(() => {
        if (this.captureManager) {
          this.captureManager.onFrame();
        }
      });

      this.renderer.start();
    } else {
      this.errorDisplay.show('Failed to initialize WebGPU renderer.');
    }
  }

  setupUIEvents() {
    this.uiEvents = new UIEvents({
      onTogglePause: () => this.renderer?.togglePause(),
      onToggleFullscreen: () => this.layoutManager?.toggleFullscreen(),
      onExitFullscreen: () => {
        if (this.layoutManager?.isFullscreen) {
          this.layoutManager.toggleFullscreen();
        }
      },
      onShare: () => this.shareUrl(),
      onOpenDownloadModal: () => this.uiEvents.openModal(),
      onScreenshot: async () => {
        if (!this.captureManager) return;
        const result = await this.captureManager.screenshot();
        if (!result.success) {
          this.errorDisplay.show(`Screenshot failed: ${result.error}`);
        }
      },
      onStartRecording: (maxFrames) => {
        if (!this.captureManager) return;
        const result = this.captureManager.startRecording(maxFrames);
        if (!result.success) {
          this.errorDisplay.show(`Recording failed: ${result.error}`);
        }
      },
      onStopRecording: () => {
        this.captureManager?.stopRecording();
      },
      isRecording: () => this.captureManager?.isRecording ?? false,
      // Texture handlers
      onLoadTextureFile: async (file, channel) => {
        const textureManager = this.renderer?.getTextureManager();
        if (!textureManager) return;

        const result = await textureManager.loadFromFile(file, channel);
        if (result.success) {
          this.updateTextureUI(channel, file);
        } else {
          this.errorDisplay.show(`Texture load failed: ${result.error}`);
        }
      },
      onGenerateNoise: (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        if (!textureManager) return;

        const result = textureManager.generateNoiseTexture(channel);
        if (result.success) {
          this.updateTextureUIForPreset(channel, 'noise');
        } else {
          this.errorDisplay.show(`Noise generation failed: ${result.error}`);
        }
      },
      onGenerateGradient: (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        if (!textureManager) return;

        const result = textureManager.generateGradientTexture(channel);
        if (result.success) {
          this.updateTextureUIForPreset(channel, 'gradient');
        } else {
          this.errorDisplay.show(`Gradient generation failed: ${result.error}`);
        }
      },
      onGenerateChecker: (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        if (!textureManager) return;

        const result = textureManager.generateCheckerTexture(channel);
        if (result.success) {
          this.updateTextureUIForPreset(channel, 'checker');
        } else {
          this.errorDisplay.show(`Checker generation failed: ${result.error}`);
        }
      },
      onStartCamera: async (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        if (!textureManager) return;

        // Toggle camera: if active, stop it
        if (textureManager.isCameraActive(channel)) {
          textureManager.clearChannel(channel);
          this.uiEvents.updateTextureSlot(channel, null);
          this.uiEvents.updateTexturePreview(channel);
          this.uiEvents.updateCameraButtonState(false);
        } else {
          const result = await textureManager.startCamera(channel);
          if (result.success) {
            this.updateTextureUIForPreset(channel, 'camera');
            this.uiEvents.updateCameraButtonState(true);
          } else {
            this.errorDisplay.show(`Camera failed: ${result.error}`);
          }
        }
      },
      onFlipCamera: async (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        if (!textureManager) return;

        const result = await textureManager.toggleCameraFacing(channel);
        if (result.success) {
          this.updateTextureUIForPreset(channel, 'camera');
        } else {
          this.errorDisplay.show(`Camera flip failed: ${result.error}`);
        }
      },
      isCameraActive: (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        return textureManager?.isCameraActive(channel) ?? false;
      },
      onClearTexture: (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        if (!textureManager) return;

        textureManager.clearChannel(channel);
        this.uiEvents.updateTextureSlot(channel, null);
        this.uiEvents.updateTexturePreview(channel);
      },
      getTextureInfo: (channel) => {
        const textureManager = this.renderer?.getTextureManager();
        return textureManager?.getChannelInfo(channel) ?? null;
      },
      // Pass management handlers
      onPassChange: (passId) => this.switchPass(passId),
      onAddBuffer: () => this.addBuffer(),
      onDeleteBuffer: (passId) => this.deleteBuffer(passId),
      // Sample loading
      onLoadSample: (sampleId) => this.loadSample(sampleId),
      // Uniform handlers
      onAddUniform: (definition) => this.addUniform(definition),
      onRemoveUniform: (name) => this.removeUniform(name),
      onUniformValueChange: (name, value) => this.setUniformValue(name, value),
      onCopyDeclaration: () => this.copyUniformDeclaration(),
      onToggleUniformsSetting: () => this.toggleUniformsSetting(),
      getUniformManager: () => this.uniformManager,
    });
    this.uiEvents.init();

    // Apply uniform panel visibility from settings
    this.uiEvents.setUniformsPanelVisible(this.settings.showUniforms);

    // Set up uniform change callback (called when uniforms are added/removed)
    this.uniformManager.setOnChange(() => {
      this.uiEvents.refreshUniformList();
    });
  }

  /**
   * Switch to a different pass (tab)
   * @param {string} passId - Pass ID
   */
  switchPass(passId) {
    // Check if this pass exists
    if (!this.passManager.getPass(passId)) {
      // Pass doesn't exist (e.g., bufferB not yet added)
      return;
    }

    // Save current editor content to current pass
    const currentPass = this.passManager.getActivePass();
    const currentCode = this.editor.getCode();
    this.passManager.setCode(currentPass, currentCode);

    // Switch active pass
    this.passManager.setActivePass(passId);

    // Update UI
    this.uiEvents.setActivePassTab(passId);

    // Load code for new pass
    let code = this.passManager.getCode(passId);
    if (!code) {
      code = this.getDefaultCodeForPass(passId);
      this.passManager.setCode(passId, code);
    }
    this.editor.setCode(code);

    // Clear errors from editor (will be re-shown if pass has errors)
    this.editor.clearErrors();
    if (this.passManager.hasError(passId)) {
      // Show error state
      this.errorDisplay.show('This pass has compilation errors');
    } else {
      this.errorDisplay.clear();
    }
  }

  /**
   * Add a new buffer pass
   */
  addBuffer() {
    const newBufferId = this.passManager.addNextBuffer();
    if (!newBufferId) return; // All buffers already added

    // Set default code for new buffer
    this.passManager.setCode(newBufferId, DEFAULT_BUFFER_SHADER);

    // Add tab in UI
    const letter = PassManager.getBufferLetter(newBufferId);
    this.uiEvents.addBufferTab(newBufferId, letter);

    // Update add button visibility
    this.uiEvents.setAddBufferButtonVisible(this.passManager.canAddBuffer());

    // Update renderer enabled buffers
    this.renderer?.setEnabledBuffers(this.passManager.getEnabledBuffers());

    // Switch to the new buffer
    this.switchPass(newBufferId);

    // Compile the new buffer
    this.compilePass(newBufferId, DEFAULT_BUFFER_SHADER);
  }

  /**
   * Delete a buffer pass
   * @param {string} passId - Buffer ID to delete
   */
  deleteBuffer(passId) {
    // Don't allow deleting Main pass
    if (passId === PASS_IDS.MAIN) return;

    // Check if buffer exists
    if (!this.passManager.isBufferEnabled(passId)) return;

    // Check if currently viewing this buffer
    const wasActive = this.passManager.getActivePass() === passId;

    // Remove from pass manager (this also switches to Main if needed)
    this.passManager.removeBuffer(passId);

    // Remove tab from UI
    this.uiEvents.removeBufferTab(passId);

    // Update add button visibility
    this.uiEvents.setAddBufferButtonVisible(this.passManager.canAddBuffer());

    // Update renderer enabled buffers
    this.renderer?.setEnabledBuffers(this.passManager.getEnabledBuffers());

    // If this buffer was active, switch to Main
    if (wasActive) {
      this.uiEvents.setActivePassTab(PASS_IDS.MAIN);
      const mainCode = this.passManager.getCode(PASS_IDS.MAIN) || DEFAULT_SHADER;
      this.editor.setCode(mainCode);

      // Update error display for Main pass
      this.editor.clearErrors();
      if (this.passManager.hasError(PASS_IDS.MAIN)) {
        this.errorDisplay.show('This pass has compilation errors');
      } else {
        this.errorDisplay.clear();
      }
    }

    // Update URL state
    this.updateUrlState();
  }

  /**
   * Load a sample shader
   * @param {string} sampleId - Sample ID
   */
  async loadSample(sampleId) {
    const sample = getSampleById(sampleId);
    if (!sample) {
      this.errorDisplay.show(`Sample not found: ${sampleId}`);
      return;
    }

    // Reset pass manager state
    this.passManager.reset();

    // Clear uniforms when loading a sample
    this.uniformManager.clear();
    this.uiEvents.refreshUniformList();

    // Reset buffer tabs in UI
    this.resetBufferTabs();

    // Set main pass code
    this.passManager.setCode(PASS_IDS.MAIN, sample.passes.main);

    // Add buffers as needed (in order: A, B, C, D)
    if (sample.passes.bufferA) {
      this.passManager.addNextBuffer();
      this.passManager.setCode(PASS_IDS.BUFFER_A, sample.passes.bufferA);
      this.uiEvents.addBufferTab(PASS_IDS.BUFFER_A, 'A');
    }
    if (sample.passes.bufferB) {
      this.passManager.addNextBuffer();
      this.passManager.setCode(PASS_IDS.BUFFER_B, sample.passes.bufferB);
      this.uiEvents.addBufferTab(PASS_IDS.BUFFER_B, 'B');
    }
    if (sample.passes.bufferC) {
      this.passManager.addNextBuffer();
      this.passManager.setCode(PASS_IDS.BUFFER_C, sample.passes.bufferC);
      this.uiEvents.addBufferTab(PASS_IDS.BUFFER_C, 'C');
    }
    if (sample.passes.bufferD) {
      this.passManager.addNextBuffer();
      this.passManager.setCode(PASS_IDS.BUFFER_D, sample.passes.bufferD);
      this.uiEvents.addBufferTab(PASS_IDS.BUFFER_D, 'D');
    }

    // Update add button visibility
    this.uiEvents.setAddBufferButtonVisible(this.passManager.canAddBuffer());

    // Update renderer enabled buffers
    this.renderer?.setEnabledBuffers(this.passManager.getEnabledBuffers());

    // Set active pass to main and update editor
    this.passManager.setActivePass(PASS_IDS.MAIN);
    this.uiEvents.setActivePassTab(PASS_IDS.MAIN);
    this.editor.setCode(sample.passes.main);

    // Clear any existing errors
    this.errorDisplay.clear();
    this.editor.clearErrors();

    // Compile all passes
    await this.compileAllPasses();

    // Update URL with new state
    this.updateUrlState();
  }

  /**
   * Reset buffer tabs to initial state (Main only)
   */
  resetBufferTabs() {
    const passTabs = document.getElementById('pass-tabs');
    if (!passTabs) return;

    // Remove all buffer tabs (keep only Main)
    const tabs = passTabs.querySelectorAll('.pass-tab');
    tabs.forEach((tab) => {
      const passId = tab.dataset.pass;
      if (passId !== 'main') {
        tab.remove();
      }
    });

    // Clear error states on remaining tabs
    const remainingTabs = passTabs.querySelectorAll('.pass-tab');
    remainingTabs.forEach((tab) => {
      tab.classList.remove('has-error');
    });
  }

  /**
   * Update texture UI after loading an image file
   */
  async updateTextureUI(channel, file) {
    // Create thumbnail from file
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      this.uiEvents.updateTextureSlot(channel, { source: file.name }, dataUrl);
      this.uiEvents.setTexturePreviewImage(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  /**
   * Update texture UI for preset textures
   */
  updateTextureUIForPreset(channel, presetName) {
    const info = this.renderer?.getTextureManager()?.getChannelInfo(channel);
    if (info) {
      const dataUrl = generatePresetPreview(presetName);
      this.uiEvents.updateTextureSlot(channel, info, dataUrl);
      this.uiEvents.setTexturePreviewImage(dataUrl);
    }
  }

  setupCaptureCallbacks() {
    this.captureManager.onRecordingStart = () => {
      this.uiEvents.setRecordingState(true, this.captureManager.maxFrames);
    };

    this.captureManager.onRecordingStop = () => {
      this.uiEvents.setRecordingState(false);
    };

    this.captureManager.onFrameUpdate = (current, max) => {
      this.uiEvents.updateRecordingProgress(current, max);
    };
  }

  initThemeSelector() {
    const select = document.getElementById('select-theme');
    if (!select) return;

    // Set initial value from settings
    select.value = this.settings.editorTheme;

    // Handle theme change
    select.addEventListener('change', (e) => {
      const themeId = e.target.value;
      this.editor.setTheme(themeId);
      updateSetting('editorTheme', themeId);
    });
  }

  async shareUrl() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      this.uiEvents.showButtonFeedback('btn-share', 'Copied!');
    } catch (err) {
      this.errorDisplay.show(`Failed to copy URL: ${err.message}`);
    }
  }

  /**
   * Compile the currently active pass (called by editor auto-compile)
   * @param {string} code - Slang source code
   */
  async compile(code) {
    const activePass = this.passManager.getActivePass();
    await this.compilePass(activePass, code);
  }

  /**
   * Compile a specific pass
   * @param {string} passId - Pass ID
   * @param {string} code - Slang source code
   */
  async compilePass(passId, code) {
    if (!this.compiler) {
      this.errorDisplay.show('Compiler not initialized.');
      return;
    }

    console.log(`Compiling ${passId}...`, code.length, 'chars');

    // Save code to pass manager
    this.passManager.setCode(passId, code);

    const result = this.compiler.compile(code);

    if (result.success) {
      this.passManager.setWgslCode(passId, result.code);
      this.passManager.setError(passId, false);

      if (passId === this.passManager.getActivePass()) {
        this.editor.clearErrors();
      }

      // Update tab error state
      this.uiEvents.setPassTabError(passId, false);

      // Update WebGPU renderer with new shader
      if (this.renderer) {
        const renderResult = await this.renderer.updatePassShader(passId, result.code);
        if (renderResult.success) {
          // Only clear error display if this is the active pass
          if (passId === this.passManager.getActivePass()) {
            this.errorDisplay.clear();
          }
          // Update URL with all pass states
          this.updateUrlState();
        } else {
          if (passId === this.passManager.getActivePass()) {
            this.errorDisplay.show(`WGSL Error:\n${renderResult.error}`);
          }
          this.passManager.setError(passId, true);
          this.uiEvents.setPassTabError(passId, true);
        }
      } else {
        if (passId === this.passManager.getActivePass()) {
          this.errorDisplay.clear();
        }
        this.updateUrlState();
      }
    } else {
      const errorMsg = result.diagnostics || result.error || 'Unknown error';
      this.passManager.setError(passId, true);
      this.passManager.setWgslCode(passId, '');

      // Update tab error state
      this.uiEvents.setPassTabError(passId, true);

      // Only show error and highlight if this is the active pass
      if (passId === this.passManager.getActivePass()) {
        this.errorDisplay.show(errorMsg);

        // Highlight error lines in editor
        if (result.errorLines && result.errorLines.length > 0) {
          this.editor.setErrorLines(result.errorLines);
        }
      }
    }
  }

  /**
   * Compile all enabled passes
   */
  async compileAllPasses() {
    const passes = this.passManager.getEnabledPasses();
    for (const passId of passes) {
      const code = this.passManager.getCode(passId) || this.getDefaultCodeForPass(passId);
      await this.compilePass(passId, code);
    }
  }

  /**
   * Update URL with current multi-pass state
   */
  updateUrlState() {
    // Save current editor content to active pass
    const activePass = this.passManager.getActivePass();
    const currentCode = this.editor.getCode();
    this.passManager.setCode(activePass, currentCode);

    // Serialize pass manager state
    const state = this.passManager.serialize();

    // Add uniforms to state
    const uniforms = this.uniformManager.serialize();
    if (uniforms.length > 0) {
      state.u = uniforms;
    }

    updateUrlMultiPass(state);
  }

  // Uniform methods
  /**
   * Add a custom uniform
   * @param {Object} definition
   * @returns {{ success: boolean, error?: string }}
   */
  addUniform(definition) {
    const result = this.uniformManager.add(definition);
    if (result.success) {
      // refreshUniformList is called by onChange callback
      this.updateUrlState();
    }
    return result;
  }

  /**
   * Remove a custom uniform
   * @param {string} name
   */
  removeUniform(name) {
    if (this.uniformManager.remove(name)) {
      // refreshUniformList is called by onChange callback
      this.updateUrlState();
    }
  }

  /**
   * Set uniform value
   * @param {string} name
   * @param {number|number[]} value
   */
  setUniformValue(name, value) {
    this.uniformManager.setValue(name, value);
    // Don't update URL on every value change - too expensive
    // URL will be updated on next compile or explicit action
  }

  /**
   * Toggle uniforms panel visibility setting
   * @returns {boolean} New visibility state
   */
  toggleUniformsSetting() {
    const newValue = !this.settings.showUniforms;
    this.settings.showUniforms = newValue;
    updateSetting('showUniforms', newValue);
    this.uiEvents.setUniformsPanelVisible(newValue);
    return newValue;
  }

  /**
   * Copy uniform struct declaration to clipboard
   */
  async copyUniformDeclaration() {
    const declaration = this.uniformManager.generateDeclaration();
    if (!declaration) {
      this.uiEvents.showCopyFeedback('No uniforms');
      return;
    }

    const fullStruct = `struct Uniforms {
  float2 resolution;
  float time;
  float _padding;
  float2 mouse;
  float2 _padding2;
${declaration}
};`;

    try {
      await navigator.clipboard.writeText(fullStruct);
      this.uiEvents.showCopyFeedback('Copied!');
    } catch (err) {
      this.errorDisplay.show(`Failed to copy: ${err.message}`);
    }
  }
}

// Initialize app
const app = new App();
app.init().catch((err) => {
  console.error('Failed to initialize app:', err);
  const errorOverlay = document.getElementById('error-overlay');
  const errorContent = document.getElementById('error-content');
  if (errorOverlay && errorContent) {
    errorContent.textContent = `Failed to initialize: ${err.message}`;
    errorOverlay.classList.remove('hidden');
  }
});
